import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {EntrevistaTranscriptView} from '@/components/entrevista-transcript-view'
import {
  useEmployeeEntrevistaVersion,
  useMyEntrevistaVersion,
  type EntrevistaVersion,
} from '@/hooks/sgsst/riesgos/use-entrevista'
import {formatDate} from '@/lib/risk'

/** Sin consulta propia: cada envoltorio llama su hook, que rules-of-hooks no deja pasar como prop. */
function VersionRow({
  businessId,
  employeeId,
  version,
  expanded,
  onToggle,
  entrevista,
  isLoading,
}: {
  businessId: string
  employeeId?: string
  version: EntrevistaVersion
  expanded: boolean
  onToggle: () => void
  entrevista: ReturnType<typeof useMyEntrevistaVersion>['data']
  isLoading: boolean
}) {
  const {t} = useTranslation()
  const date = version.completed_at ?? version.superseded_at
  return (
    <div className="border-t border-border py-3 first:border-t-0" data-testid={`entrevista-version-${version.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground">
            {date
              ? t(
                  version.vigente
                    ? 'employees.detail.evaluacionHistorialVersionVigente'
                    : 'employees.detail.evaluacionHistorialVersion',
                  {date: formatDate(date)}
                )
              : '—'}
          </span>
          <Tag tone="neutral">
            {t(
              version.modo === 'reconfirmacion'
                ? 'employees.detail.evaluacionHistorialModoReconfirmacion'
                : 'employees.detail.evaluacionHistorialModoNormal'
            )}
          </Tag>
        </div>
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {t(expanded ? 'employees.detail.evaluacionHistorialCollapse' : 'employees.detail.evaluacionHistorialExpand')}
        </Button>
      </div>
      {expanded && (
        <div className="mt-3">
          <EntrevistaTranscriptView
            businessId={businessId}
            employeeId={employeeId}
            entrevista={entrevista}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  )
}

export function OwnerEntrevistaVersionRow({
  businessId,
  employeeId,
  version,
}: {
  businessId: string
  employeeId: string
  version: EntrevistaVersion
}) {
  const [expanded, setExpanded] = useState(false)
  const {data: entrevista, isLoading} = useEmployeeEntrevistaVersion(
    businessId,
    employeeId,
    expanded ? version.id : undefined
  )
  return (
    <VersionRow
      businessId={businessId}
      employeeId={employeeId}
      version={version}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      entrevista={entrevista}
      isLoading={isLoading}
    />
  )
}

export function MyEntrevistaVersionRow({businessId, version}: {businessId: string; version: EntrevistaVersion}) {
  const [expanded, setExpanded] = useState(false)
  const {data: entrevista, isLoading} = useMyEntrevistaVersion(businessId, expanded ? version.id : undefined)
  return (
    <VersionRow
      businessId={businessId}
      version={version}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      entrevista={entrevista}
      isLoading={isLoading}
    />
  )
}
